<template>
  <div class="facility-selector">
    <!-- Inline Mode (for navigation bar) -->
    <div v-if="mode === 'inline'" class="d-flex align-items-center">
      <i class="mdi mdi-hospital-building me-2 text-primary"></i>
      <select 
        v-model="selectedFacilityID" 
        @change="handleFacilityChange"
        class="form-select form-select-sm facility-select"
        :disabled="loading"
      >
        <option value="">-- Select Facility --</option>
        <option 
          v-for="facility in facilities" 
          :key="facility.facilityID" 
          :value="facility.facilityID"
        >
          {{ facility.facilityName }}
        </option>
      </select>
    </div>

    <!-- Card Mode (for dashboard/pages) -->
    <div v-else-if="mode === 'card'" class="card">
      <div class="card-body">
        <div class="d-flex align-items-center mb-3">
          <div class="flex-shrink-0 me-3">
            <div class="avatar-sm">
              <span class="avatar-title bg-primary-subtle text-primary rounded">
                <i class="mdi mdi-hospital-building font-size-24"></i>
              </span>
            </div>
          </div>
          <div class="flex-grow-1">
            <h5 class="mb-1">Select Facility</h5>
            <p class="text-muted mb-0">Choose the facility to work with</p>
          </div>
        </div>

        <div v-if="message" class="alert alert-danger alert-sm" role="alert">
          {{ message }}
        </div>

        <div v-if="loading" class="text-center py-3">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <span class="ms-2">Loading facilities...</span>
        </div>

        <select 
          v-else
          v-model="selectedFacilityID" 
          @change="handleFacilityChange"
          class="form-select"
        >
          <option value="">-- Select Facility --</option>
          <option 
            v-for="facility in facilities" 
            :key="facility.facilityID" 
            :value="facility.facilityID"
          >
            {{ facility.facilityName }} ({{ facility.facilityType }})
          </option>
        </select>

        <!-- Selected Facility Details -->
        <div v-if="selectedFacility" class="mt-3 p-3 bg-light rounded">
          <div class="row">
            <div class="col-6">
              <small class="text-muted">Facility Code</small>
              <p class="mb-1 fw-semibold">{{ selectedFacility.facilityCode }}</p>
            </div>
            <div class="col-6">
              <small class="text-muted">Facility Level</small>
              <p class="mb-1 fw-semibold">Level {{ selectedFacility.facilityLevel }}</p>
            </div>
            <div class="col-6">
              <small class="text-muted">Sub County</small>
              <p class="mb-1 fw-semibold">{{ selectedFacility.subCountyName || '-' }}</p>
            </div>
            <div class="col-6">
              <small class="text-muted">Ward</small>
              <p class="mb-0 fw-semibold">{{ selectedFacility.wardName || '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dropdown Mode (for headers) -->
    <div v-else class="dropdown">
      <button 
        class="btn btn-light dropdown-toggle d-flex align-items-center" 
        type="button" 
        id="facilityDropdown" 
        data-bs-toggle="dropdown" 
        aria-expanded="false"
      >
        <i class="mdi mdi-hospital-building me-2"></i>
        <span v-if="selectedFacility">{{ selectedFacility.facilityName }}</span>
        <span v-else>Select Facility</span>
      </button>
      <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="facilityDropdown">
        <li v-if="loading" class="dropdown-item text-center">
          <div class="spinner-border spinner-border-sm" role="status"></div>
          Loading...
        </li>
        <li v-else-if="facilities.length === 0" class="dropdown-item text-muted">
          No facilities available
        </li>
        <li v-for="facility in facilities" :key="facility.facilityID">
          <a 
            class="dropdown-item" 
            :class="{ 'active': selectedFacilityID === facility.facilityID }"
            href="#" 
            @click.prevent="selectFacility(facility)"
          >
            <i class="mdi mdi-hospital-marker me-2"></i>
            {{ facility.facilityName }}
            <small class="text-muted d-block">{{ facility.facilityType }}</small>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, hospital } from "@/api";

export default {
  name: "FacilitySelector",

  props: {
    // Mode: 'inline', 'card', 'dropdown'
    mode: {
      type: String,
      default: 'inline'
    },
    // Pre-select a facility
    initialFacilityID: {
      type: [String, Number],
      default: null
    },
    // Auto-load on mount
    autoLoad: {
      type: Boolean,
      default: true
    }
  },

  emits: ['facility-changed', 'facilities-loaded'],

  data() {
    return {
      loading: false,
      message: null,
      facilities: [],
      selectedFacilityID: '',
      selectedFacility: null
    }
  },

  watch: {
    initialFacilityID: {
      handler(newVal) {
        if (newVal && this.facilities.length > 0) {
          this.selectedFacilityID = newVal;
          this.setSelectedFacility();
        }
      },
      immediate: true
    }
  },

  mounted() {
    // Check if facility is already stored in session
    const storedFacilityID = sessionStorage.getItem("hospitalFacilityID");
    if (storedFacilityID) {
      this.selectedFacilityID = storedFacilityID;
    }

    if (this.autoLoad) {
      this.getFacilities();
    }
  },

  methods: {
    getFacilities() {
      this.loading = true;
      this.message = null;

      const data = new FormData();
      data.append("function", "getFacilities");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital)
        .then((res) => {
          this.loading = false;
          if (res.data.success) {
            this.facilities = res.data.data.facilities;
            this.$emit('facilities-loaded', this.facilities);

            // If we have a stored facility ID, set the selected facility
            if (this.selectedFacilityID) {
              this.setSelectedFacility();
            }
            // If only one facility, auto-select it
            else if (this.facilities.length === 1) {
              this.selectedFacilityID = this.facilities[0].facilityID;
              this.setSelectedFacility();
              this.emitChange();
            }
          } else {
            this.message = res.data.message;
          }
        })
        .catch((e) => {
          this.loading = false;
          this.message = e.message;
        });
    },

    handleFacilityChange() {
      this.setSelectedFacility();
      this.emitChange();
    },

    selectFacility(facility) {
      this.selectedFacilityID = facility.facilityID;
      this.selectedFacility = facility;
      this.emitChange();
    },

    setSelectedFacility() {
      if (this.selectedFacilityID) {
        this.selectedFacility = this.facilities.find(
          f => f.facilityID == this.selectedFacilityID
        );
      } else {
        this.selectedFacility = null;
      }
    },

    emitChange() {
      // Store in session for persistence
      if (this.selectedFacility) {
        sessionStorage.setItem("hospitalFacilityID", this.selectedFacility.facilityID);
        sessionStorage.setItem("hospitalFacilityName", this.selectedFacility.facilityName);
        sessionStorage.setItem("hospitalFacilityCode", this.selectedFacility.facilityCode);
      } else {
        sessionStorage.removeItem("hospitalFacilityID");
        sessionStorage.removeItem("hospitalFacilityName");
        sessionStorage.removeItem("hospitalFacilityCode");
      }

      this.$emit('facility-changed', this.selectedFacility);
    },

    // Public method to refresh facilities
    refresh() {
      this.getFacilities();
    },

    // Public method to get current facility
    getCurrentFacility() {
      return this.selectedFacility;
    },

    // Public method to clear selection
    clearSelection() {
      this.selectedFacilityID = '';
      this.selectedFacility = null;
      sessionStorage.removeItem("hospitalFacilityID");
      sessionStorage.removeItem("hospitalFacilityName");
      sessionStorage.removeItem("hospitalFacilityCode");
      this.$emit('facility-changed', null);
    }
  }
}
</script>

<style scoped>
.facility-selector {
  min-width: 200px;
}

.facility-select {
  min-width: 180px;
  border-radius: 4px;
}

.dropdown-menu {
  min-width: 250px;
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item.active {
  background-color: #556ee6;
  color: #fff;
}

.dropdown-item small {
  font-size: 11px;
}

.alert-sm {
  padding: 0.5rem 1rem;
  font-size: 13px;
}

.avatar-sm {
  width: 3rem;
  height: 3rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.bg-primary-subtle {
  background-color: rgba(85, 110, 230, 0.1);
}
</style>
